import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chapterAPI, courseAPI } from '../services/api';

function ChapterProgressList() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chaptersData, setChaptersData] = useState(null);
  const [assetProgress, setAssetProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [activeTab, setActiveTab] = useState('students'); // students or chapters
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/courses');
      return;
    }
    fetchChapters();
    fetchAssetProgress();
  }, [courseId, user, navigate]);

  const fetchChapters = async () => {
    try {
      setLoading(true);
      const response = await chapterAPI.getChaptersWithStats(courseId);
      setChaptersData(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch chapters');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetProgress = async () => {
    try {
      setLoadingProgress(true);
      const response = await courseAPI.getAssetProgressMatrix(courseId);
      setAssetProgress(response.data);
    } catch (err) {
      console.error('Error fetching asset progress:', err);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleChapterClick = (chapterId) => {
    navigate(`/courses/${courseId}/chapters/${chapterId}/progress-matrix`);
  };

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#667eea', fontSize: '16px' }}>Loading chapters...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <div className="error">{error}</div>
          <button 
            onClick={() => navigate('/courses')}
            className="btn btn-secondary"
          >
            ← Back to Courses
          </button>
        </div>
      </div>
    );
  }

  if (!chaptersData) {
    return (
      <div className="container" style={{ paddingTop: '20px' }}>
        <div className="card">
          <p>No data found</p>
          <button 
            onClick={() => navigate('/courses')}
            className="btn btn-secondary"
          >
            ← Back to Courses
          </button>
        </div>
      </div>
    );
  }

  const { course, chapters } = chaptersData;

  return (
    <div className="container" style={{ paddingTop: '20px' }}>
      <div className="card">
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <button
            onClick={() => navigate('/courses')}
            style={{
              padding: '6px 12px',
              background: '#f7fafc',
              color: '#667eea',
              border: '1px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              marginBottom: '16px'
            }}
          >
            ← Back to Courses
          </button>
          
          <h2 style={{ 
            color: '#2d3748', 
            fontSize: '24px', 
            fontWeight: '700',
            marginBottom: '8px'
          }}>
            Course Progress Tracking
          </h2>
          <p style={{ 
            color: '#718096', 
            fontSize: '15px',
            marginBottom: '0'
          }}>
            {course.title}
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          gap: '0',
          borderBottom: '2px solid #e2e8f0',
          marginBottom: '24px'
        }}>
          {['students', 'chapters'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '12px 24px',
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #667eea' : '2px solid transparent',
                color: activeTab === tab ? '#667eea' : '#718096',
                fontWeight: activeTab === tab ? '600' : '400',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                marginBottom: '-2px'
              }}
            >
              {tab === 'students' ? '👥 Student-wise Progress' : '📚 Chapter-wise Progress'}
            </button>
          ))}
        </div>

        {/* Search Bar */}
        {activeTab === 'students' && (
          <div style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="Search students by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                fontSize: '14px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                outline: 'none'
              }}
            />
          </div>
        )}

        {/* Student-wise Progress Tab */}
        {activeTab === 'students' && (
          <div>
            {loadingProgress ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p style={{ color: '#718096', fontSize: '14px' }}>Loading progress data...</p>
              </div>
            ) : assetProgress?.studentsProgress?.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '40px', 
                background: '#f7fafc', 
                borderRadius: '12px', 
                border: '2px dashed #cbd5e0' 
              }}>
                <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>📚</p>
                <p style={{ color: '#718096', margin: 0 }}>No students enrolled in this course.</p>
              </div>
            ) : assetProgress ? (
              <>
                {/* Detailed Progress Section */}
                <div style={{ marginBottom: '16px' }}>
                  <h3 style={{
                    margin: '0 0 4px 0',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#2d3748'
                  }}>
                    📊 Detailed Progress
                  </h3>
                  <p style={{
                    margin: 0,
                    fontSize: '13px',
                    color: '#718096'
                  }}>
                    Click on any student to view their detailed course progress
                  </p>
                </div>

                {(() => {
                  const filteredStudents = assetProgress.studentsProgress
                    .filter(student => {
                      if (!searchQuery) return true;
                      const searchLower = searchQuery.toLowerCase();
                      return student.studentName.toLowerCase().includes(searchLower) ||
                             student.studentEmail.toLowerCase().includes(searchLower);
                    })
                    .sort((a, b) => a.studentName.localeCompare(b.studentName));

                  return filteredStudents.length === 0 ? (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px', 
                      background: '#f7fafc', 
                      borderRadius: '12px', 
                      border: '2px dashed #cbd5e0',
                      marginBottom: '24px'
                    }}>
                      <p style={{ fontSize: '36px', margin: '0 0 12px 0' }}>🔍</p>
                      <p style={{ color: '#718096', margin: 0 }}>
                        No students found matching "{searchQuery}"
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '12px', marginBottom: '24px' }}>
                      {filteredStudents.map(student => {
                        const completedCount = student.assetCompletions.filter(ac => ac.completed).length;
                        const totalAssets = student.assetCompletions.length;
                        const percentage = totalAssets > 0 ? Math.round((completedCount / totalAssets) * 100) : 0;
                        
                        const completionTimestamps = student.assetCompletions
                          .filter(ac => ac.completed && ac.completionData?.completedAt)
                          .map(ac => new Date(ac.completionData.completedAt).getTime())
                          .filter(t => !isNaN(t));
                        
                        const lastActivityTimestamp = completionTimestamps.length > 0 
                          ? Math.max(...completionTimestamps) 
                          : null;
                        
                        const formatLastActivity = (timestamp) => {
                          if (!timestamp) return null;
                          const now = Date.now();
                          const diffMs = now - timestamp;
                          const diffMins = Math.floor(diffMs / 60000);
                          const diffHours = Math.floor(diffMs / 3600000);
                          const diffDays = Math.floor(diffMs / 86400000);
                          
                          if (diffMins < 1) return 'Just now';
                          if (diffMins < 60) return `${diffMins}m ago`;
                          if (diffHours < 24) return `${diffHours}h ago`;
                          if (diffDays < 7) return `${diffDays}d ago`;
                          if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
                          if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
                          return `${Math.floor(diffDays / 365)}y ago`;
                        };
                        
                        const getProgressColor = (pct) => {
                          if (pct === 100) return '#22c55e';
                          if (pct >= 75) return '#3b82f6';
                          if (pct >= 50) return '#f59e0b';
                          if (pct >= 25) return '#f97316';
                          return '#ef4444';
                        };

                        return (
                          <div 
                            key={student.studentId}
                            onClick={() => navigate(`/course/${courseId}/student/${student.studentId}/progress`)}
                            style={{
                              padding: '20px',
                              background: '#f7fafc',
                              borderRadius: '12px',
                              border: '1px solid #e2e8f0',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              gap: '20px'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#edf2f7';
                              e.currentTarget.style.borderColor = '#667eea';
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#f7fafc';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <h4 style={{ 
                                margin: '0 0 4px 0', 
                                fontSize: '16px', 
                                color: '#667eea',
                                fontWeight: '600'
                              }}>
                                👤 {student.studentName}
                              </h4>
                              <p style={{ 
                                margin: '0', 
                                fontSize: '13px', 
                                color: '#718096' 
                              }}>
                                {student.studentEmail}
                              </p>
                              {lastActivityTimestamp && (
                                <p style={{ 
                                  margin: '4px 0 0 0', 
                                  fontSize: '12px', 
                                  color: '#a0aec0',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  🕒 Last activity: {formatLastActivity(lastActivityTimestamp)}
                                </p>
                              )}
                              {student.enrollmentStatus === 'completed' && (
                                <p style={{ 
                                  margin: '4px 0 0 0', 
                                  fontSize: '12px', 
                                  color: '#22c55e',
                                  fontWeight: '600'
                                }}>
                                  ✓ Course Completed
                                </p>
                              )}
                            </div>
                            
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '20px',
                              minWidth: '200px'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  marginBottom: '6px'
                                }}>
                                  <span style={{ 
                                    fontSize: '12px', 
                                    color: '#718096',
                                    fontWeight: '500'
                                  }}>
                                    Progress
                                  </span>
                                  <span style={{ 
                                    fontSize: '14px', 
                                    fontWeight: '700',
                                    color: getProgressColor(percentage)
                                  }}>
                                    {percentage}%
                                  </span>
                                </div>
                                <div style={{
                                  width: '100%',
                                  height: '8px',
                                  background: '#e2e8f0',
                                  borderRadius: '4px',
                                  overflow: 'hidden'
                                }}>
                                  <div style={{
                                    width: `${percentage}%`,
                                    height: '100%',
                                    background: getProgressColor(percentage),
                                    transition: 'width 0.3s ease',
                                    borderRadius: '4px'
                                  }} />
                                </div>
                                <p style={{ 
                                  margin: '4px 0 0 0', 
                                  fontSize: '11px', 
                                  color: '#a0aec0',
                                  textAlign: 'right'
                                }}>
                                  {completedCount} / {totalAssets} completed
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Summary Stats */}
                <div style={{
                  padding: '20px',
                  background: '#f7fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '20px'
                }}>
                  <div>
                    <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                      {searchQuery ? 'Showing / Total Students' : 'Total Students'}
                    </p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                      {searchQuery ? (
                        <>
                          {assetProgress.studentsProgress.filter(student => {
                            const searchLower = searchQuery.toLowerCase();
                            return student.studentName.toLowerCase().includes(searchLower) ||
                                   student.studentEmail.toLowerCase().includes(searchLower);
                          }).length} / {assetProgress.studentsProgress.length}
                        </>
                      ) : (
                        assetProgress.studentsProgress.length
                      )}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                      Total Assets
                    </p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#1a202c' }}>
                      {assetProgress.assets.length}
                    </p>
                  </div>
                  <div>
                    <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#718096', fontWeight: '500' }}>
                      Average Completion
                    </p>
                    <p style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#667eea' }}>
                      {Math.round(
                        assetProgress.studentsProgress.reduce((acc, s) => {
                          const completed = s.assetCompletions.filter(ac => ac.completed).length;
                          const total = s.assetCompletions.length;
                          return acc + (total > 0 ? (completed / total) * 100 : 0);
                        }, 0) / (assetProgress.studentsProgress.length || 1)
                      )}%
                    </p>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Chapter-wise Progress Tab */}
        {activeTab === 'chapters' && (
          <div>
            <p style={{ 
              color: '#4a5568', 
              fontSize: '14px',
              marginBottom: '20px'
            }}>
              Click on any chapter to view the detailed progress matrix showing completion status for all enrolled students.
            </p>

            {chapters.length === 0 ? (
              <div style={{
                padding: '48px 24px',
                textAlign: 'center',
                background: '#f7fafc',
                borderRadius: '12px',
                border: '2px dashed #cbd5e0'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
                <p style={{ fontSize: '16px', color: '#718096', marginBottom: '8px' }}>
                  No chapters yet
                </p>
                <p style={{ fontSize: '14px', color: '#a0aec0' }}>
                  Add chapters to this course to start tracking progress
                </p>
              </div>
            ) : (
              <div style={{ 
                display: 'grid', 
                gap: '16px'
              }}>
                {chapters.map((chapter) => (
                  <div
                    key={chapter._id}
                    onClick={() => handleChapterClick(chapter._id)}
                    style={{
                      padding: '20px',
                      background: 'white',
                      border: '2px solid #e2e8f0',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '16px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        color: '#2d3748', 
                        fontSize: '18px', 
                        fontWeight: '600',
                        marginBottom: '8px'
                      }}>
                        {chapter.title}
                      </h3>
                      {chapter.description && (
                        <p style={{ 
                          color: '#718096', 
                          fontSize: '14px',
                          marginBottom: '12px',
                          lineHeight: '1.5'
                        }}>
                          {chapter.description}
                        </p>
                      )}
                      <div style={{ 
                        display: 'flex', 
                        gap: '16px',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px' 
                        }}>
                          <span style={{ fontSize: '16px' }}>📄</span>
                          <span style={{ 
                            color: '#4a5568', 
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {chapter.assetCount} {chapter.assetCount === 1 ? 'Asset' : 'Assets'}
                          </span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px' 
                        }}>
                          <span style={{ fontSize: '16px' }}>👥</span>
                          <span style={{ 
                            color: '#4a5568', 
                            fontSize: '14px',
                            fontWeight: '500'
                          }}>
                            {chapter.enrollmentCount} {chapter.enrollmentCount === 1 ? 'Student' : 'Students'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '10px 16px',
                      background: '#667eea',
                      color: 'white',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      View Matrix →
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChapterProgressList;
